/**
 * Firestore Queries for Email Events
 *
 * Client-side Firestore operations for managing email event configurations.
 * Uses real-time listeners for automatic UI updates.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  QueryConstraint,
  Unsubscribe,
  CollectionReference,
  DocumentReference
} from "firebase/firestore";
import { firestore } from "@/base";
import type { EmailEvent, EmailEventWithId } from "./schema/email-event.types";
import type { EmailEventType } from "../../../../../types/email-event.d";

/**
 * Get email events collection reference for a licenciada
 * Collection path: /licenciadas/{licenciadaId}/email_events
 * @param licenciadaId
 */
function getEmailEventsCollection(licenciadaId: string): CollectionReference {
  return collection(firestore, `licenciadas/${licenciadaId}/email_events`);
}

/**
 * Get email event document reference
 * @param licenciadaId
 * @param eventType
 */
function getEmailEventDocument(licenciadaId: string, eventType: EmailEventType): DocumentReference {
  return doc(firestore, `licenciadas/${licenciadaId}/email_events/${eventType}`);
}

/**
 * List all email events for a licenciada
 * @param licenciadaId - Licenciada ID
 * @param onlyEnabled - Filter only enabled events (default: false)
 * @returns Promise of EmailEvent array
 */
export async function listEmailEvents(licenciadaId: string, onlyEnabled: boolean = false): Promise<EmailEventWithId[]> {
  const constraints: QueryConstraint[] = [];

  if (onlyEnabled) {
    constraints.push(where("enabled", "==", true));
  }

  const q = query(getEmailEventsCollection(licenciadaId), ...constraints);
  const snapshot = await getDocs(q);

  return snapshot.docs.map((document_) => ({
    id: document_.id,
    ...document_.data()
  })) as EmailEventWithId[];
}

/**
 * Get a specific email event by type
 * @param licenciadaId - Licenciada ID
 * @param eventType - Event type to fetch
 * @returns Promise of EmailEvent or null
 */
export async function getEmailEvent(licenciadaId: string, eventType: EmailEventType): Promise<EmailEvent | null> {
  const documentReference = getEmailEventDocument(licenciadaId, eventType);
  const snapshot = await getDoc(documentReference);

  if (!snapshot.exists()) {
    return null;
  }

  return snapshot.data() as EmailEvent;
}

/**
 * Subscribe to email events changes for a licenciada
 * Returns real-time updates as events are created/updated/deleted
 * @param licenciadaId - Licenciada ID
 * @param callback - Callback function called with updated events
 * @param onlyEnabled - Filter only enabled events (default: false)
 * @returns Unsubscribe function to stop listening
 * @example
 * ```tsx
 * const unsubscribe = subscribeToEmailEvents(
 *   licenciadaId,
 *   (events) => setEvents(events),
 *   false
 * );
 *
 * // Later, cleanup
 * unsubscribe();
 * ```
 */
export function subscribeToEmailEvents(
  licenciadaId: string,
  callback: (events: EmailEventWithId[]) => void,
  onlyEnabled: boolean = false
): Unsubscribe {
  const constraints: QueryConstraint[] = [];

  if (onlyEnabled) {
    constraints.push(where("enabled", "==", true));
  }

  const q = query(getEmailEventsCollection(licenciadaId), ...constraints);

  return onSnapshot(q, (snapshot) => {
    const events = snapshot.docs.map((document_) => ({
      id: document_.id,
      ...document_.data()
    })) as EmailEventWithId[];

    callback(events);
  });
}

/**
 * Subscribe to a specific email event
 * @param licenciadaId - Licenciada ID
 * @param eventType - Event type to watch
 * @param callback - Callback function called with updated event
 * @returns Unsubscribe function to stop listening
 */
export function subscribeToEmailEvent(
  licenciadaId: string,
  eventType: EmailEventType,
  callback: (event: EmailEvent | null) => void
): Unsubscribe {
  const documentReference = getEmailEventDocument(licenciadaId, eventType);

  return onSnapshot(documentReference, (snapshot) => {
    if (!snapshot.exists()) {
      callback(null);
      return;
    }

    callback(snapshot.data() as EmailEvent);
  });
}

/**
 * Check if an email event is configured and enabled
 * @param licenciadaId - Licenciada ID
 * @param eventType - Event type to check
 * @returns Promise of boolean
 */
export async function isEmailEventEnabled(licenciadaId: string, eventType: EmailEventType): Promise<boolean> {
  const event = await getEmailEvent(licenciadaId, eventType);
  return event?.enabled ?? false;
}
